import { useState } from "react";
import { motion } from "framer-motion";
import svgPaths from "./svg-paths";
import { pathToStateCode, stateNames } from "./state-mappings";

interface InteractiveMapProps {
  onStateClick: (stateCode: string) => void;
}

export default function InteractiveMap({ onStateClick }: InteractiveMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  const handlePathClick = (pathId: string) => {
    const stateCode = pathToStateCode[pathId];
    if (stateCode) {
      onStateClick(stateCode);
    } else {
      // For unmapped states, show the path ID so we can build the mapping
      const stateName = stateNames[hoveredState as keyof typeof stateNames] || "Unknown";
      console.log(`Unmapped state clicked: ${pathId}`);
      alert(
        `Unmapped state clicked!\n\n` +
        `Path ID: ${pathId}\n\n` +
        `Please add this mapping to state-mappings.ts:\n` +
        `${pathId}: "XX",  // State name\n\n` +
        `Copy this path ID and identify which state it is visually.`
      );
    }
  };

  const handleFederalClick = () => {
    onStateClick("FEDERAL");
  };

  // Create a compound path that covers the background but excludes the states
  // This creates a "donut" shape - outer rectangle with state-shaped holes
  const createFederalLayerPath = () => {
    // Start with outer rectangle (clockwise)
    let path = "M 0 0 L 1446 0 L 1446 1019 L 0 1019 Z";
    
    // Add all state paths as holes (they will be subtracted due to evenodd fill rule)
    Object.values(svgPaths).forEach((statePath) => {
      path += " " + statePath;
    });
    
    return path;
  };

  return (
    <div className="relative size-full">
      <svg 
        className="block size-full" 
        fill="none" 
        preserveAspectRatio="xMidYMid meet" 
        viewBox="0 0 1446 1019"
      >
        <g clipPath="url(#clip0_1_5910)">
          {/* Background */}
          <path 
            d="M1446 0H0V1018.77H1446V0Z" 
            fill="var(--fill-0, #172144)" 
          />
          
          {/* Federal Jurisdiction Layer - Ring around the states with holes for states */}
          <motion.path
            d={createFederalLayerPath()}
            fill="transparent"
            fillRule="evenodd"
            className="cursor-pointer"
            onClick={handleFederalClick}
            style={{
              pointerEvents: "auto"
            }}
          />
          
          {/* All state paths with interactive hover and click */}
          {Object.entries(svgPaths).map(([key, pathData]) => (
            <motion.path
              key={key}
              d={pathData}
              fill="var(--fill-0, #F9F9F4)"
              stroke="#172144"
              strokeWidth="1.5"
              className="cursor-pointer"
              onHoverStart={() => setHoveredState(key)}
              onHoverEnd={() => setHoveredState(null)}
              whileHover={{
                fill: "#5F648E",
                scale: 1.02,
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }
              }}
              onClick={() => handlePathClick(key)}
              style={{
                transformOrigin: "center",
                transformBox: "fill-box",
                pointerEvents: "auto"
              }}
            />
          ))}
        </g>
        <defs>
          <clipPath id="clip0_1_5910">
            <rect fill="white" height="1019" width="1446" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}